import React, { type FC, useState, useEffect } from 'react';

import Button, { type ButtonProps } from '@atlaskit/button/new';
import AddIcon from '@atlaskit/icon/core/add';
import { Flex, Box, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Skeleton from '@atlaskit/skeleton';

import type { UserPickerUserPrincipal } from '../../commonTypes';
import { useUserSuggestions } from '../../../controller/queries/useUserSuggestions';
import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../../controller/Controller';
const ENTRY_DURATION = 0.36;
const EXIT_DURATION = 0.12;
const MAX_SUGGESTIONS = 3;

const containerStyles = xcss({
	marginTop: 'space.025',
	width: 'calc(100% + 8px)',
	position: 'relative',
	top: '-4px',
	left: '-4px',
});

const entryStyles = xcss({
	flex: '0 1 auto',
	maxWidth: '42%',
	opacity: 0,
	padding: 'space.050',
	transition: `opacity ${ENTRY_DURATION}s ease-in`,
});

const baseStyles = xcss({
	flex: '0 1 auto',
	maxWidth: '42%',
	opacity: 1,
	padding: 'space.050',
	overflow: 'hidden',
	transition: `opacity ${ENTRY_DURATION}s ease-in`,
});

const exitStyles = xcss({
	flex: '0 1 auto',
	maxWidth: '0%',
	opacity: 0,
	overflow: 'hidden',
	padding: 'space.0',
	transition: `all ${EXIT_DURATION}s ease-out`,
});

const spacingWrapper = xcss({
	paddingTop: 'space.025',
	paddingBottom: 'space.025',
});

interface SuggestedUserPillProps {
	user: UserPickerUserPrincipal;
	onClick: (user: UserPickerUserPrincipal) => void;
}

const SuggestedUserPill: FC<SuggestedUserPillProps> = ({ user, onClick }) => {
	const [animationState, setAnimationState] = useState('entry');
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleUserPillClick: ButtonProps['onClick'] = (_, buttonEvent) => {
		setAnimationState('exit');
		setTimeout(() => {
			onClick(user);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					...buttonEvent.payload,
					actionSubjectId: 'SuggestedShareUserButton',
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				},
			}).fire();
		}, EXIT_DURATION * 1000);
	};

	useEffect(() => {
		setTimeout(() => {
			setAnimationState('base');
		});
	}, []);

	return (
		<Box
			xcss={
				animationState === 'entry'
					? entryStyles
					: animationState === 'exit'
						? exitStyles
						: baseStyles
			}
		>
			<Button
				spacing="compact"
				appearance="default"
				iconBefore={AddIcon}
				onClick={handleUserPillClick}
			>
				{user.name}
			</Button>
		</Box>
	);
};

const SuggestedUserPillLoader = () => {
	const [animationState, setAnimationState] = useState('entry');

	useEffect(() => {
		setTimeout(() => {
			setAnimationState('base');
		});
	}, []);

	return (
		<Box
			xcss={
				animationState === 'entry'
					? entryStyles
					: animationState === 'exit'
						? exitStyles
						: baseStyles
			}
		>
			{/* This is really for spacing so there no shift when the loading is finished */}
			<Box xcss={spacingWrapper}>
				<Skeleton width="150px" height="20px" borderRadius={4} />
			</Box>
		</Box>
	);
};

interface SuggestedShareUsersProps {
	currentPickedPrincipalIds: string[];
	onSuggestedUserClick: (selectedUser: UserPickerUserPrincipal) => void;
}

export const SuggestedShareUsers: FC<SuggestedShareUsersProps> = ({
	onSuggestedUserClick,
	currentPickedPrincipalIds,
}) => {
	const { suggestedUsers, loading } = useUserSuggestions({
		currentPickedPrincipalIds,
	});

	const isInitialLoad = loading && suggestedUsers.length === 0;

	if (!loading && suggestedUsers.length === 0) return null;

	return (
		<Flex
			wrap="nowrap"
			justifyContent="start"
			alignItems="center"
			direction="row"
			xcss={containerStyles}
		>
			{isInitialLoad
				? Array.from({ length: MAX_SUGGESTIONS }).map((_, i) => <SuggestedUserPillLoader key={i} />)
				: suggestedUsers.map((user) => (
						<SuggestedUserPill key={user.id} user={user} onClick={onSuggestedUserClick} />
					))}
		</Flex>
	);
};
